/**
 * FilterBar Component
 *
 * Unified filter bar for DataTables and list views.
 * Supports search, select, multi-select, toggle, and date-range filters.
 * Integrates with useFilterParams hook for URL synchronization.
 */

import React from 'react';
import { FilterBarProps, FilterDefinition, FILTER_WIDTH_CLASSES } from './types';
import { FilterInput } from './FilterInput';
import { FilterSelect } from './FilterSelect';
import { FilterToggle } from './FilterToggle';
import { FilterChips } from './FilterChips';

export function FilterBar({
  config,
  values,
  onChange,
  onClear,
  onClearAll,
  activeFilters = [],
  loading = false,
  className = '',
}: FilterBarProps) {
  const { filters, showClearAll = true, showActiveChips = true, layout = 'responsive' } = config;

  // Render individual filter based on type
  const renderFilter = (filter: FilterDefinition) => {
    const value = values[filter.key];
    const widthClass = filter.width ? FILTER_WIDTH_CLASSES[filter.width] : FILTER_WIDTH_CLASSES.md;

    switch (filter.type) {
      case 'search':
        return (
          <FilterInput
            key={filter.key}
            value={(value as string) || ''}
            onChange={(v) => onChange(filter.key, v)}
            placeholder={filter.placeholder}
            icon={filter.icon}
            debounceMs={filter.debounceMs ?? 300}
            disabled={filter.disabled || loading}
            className={widthClass}
          />
        );

      case 'select':
        return (
          <FilterSelect
            key={filter.key}
            value={value as string | undefined}
            onChange={(v) => onChange(filter.key, v)}
            options={filter.options || []}
            placeholder={filter.placeholder || filter.label}
            disabled={filter.disabled || loading}
            className={widthClass}
          />
        );

      case 'toggle':
        return (
          <FilterToggle
            key={filter.key}
            value={Boolean(value)}
            onChange={(v) => onChange(filter.key, v)}
            label={filter.label}
            disabled={filter.disabled || loading}
          />
        );

      case 'multi-select':
        // For now, render as select. Can be enhanced with a proper multi-select component.
        return (
          <FilterSelect
            key={filter.key}
            value={Array.isArray(value) && value.length > 0 ? value[0] : undefined}
            onChange={(v) => onChange(filter.key, v ? [v] : [])}
            options={filter.options || []}
            placeholder={filter.placeholder || filter.label}
            disabled={filter.disabled || loading}
            className={widthClass}
          />
        );

      case 'date-range':
        // Date range can be implemented as needed
        return null;

      default:
        return null;
    }
  };

  // Get layout classes
  const getLayoutClasses = () => {
    switch (layout) {
      case 'inline':
        return 'flex flex-wrap items-center gap-3';
      case 'stacked':
        return 'flex flex-col gap-3';
      case 'responsive':
      default:
        return 'flex flex-wrap items-center gap-3';
    }
  };

  // Separate mobile-visible and hidden filters
  const visibleFilters = filters.filter((f, i) => f.showOnMobile !== false || i < 2);
  const hasHiddenFilters = filters.length > visibleFilters.length;

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Main filter bar */}
      <div className={getLayoutClasses()}>
        {/* Render all filters */}
        {filters.map(renderFilter)}

        {/* Clear all button */}
        {showClearAll && activeFilters.length > 0 && onClearAll && (
          <button
            type="button"
            onClick={onClearAll}
            disabled={loading}
            className="text-sm text-gray-500 hover:text-gray-700 disabled:opacity-50"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Active filter chips */}
      {showActiveChips && activeFilters.length > 0 && (
        <FilterChips
          activeFilters={activeFilters}
          onRemove={(key) => onClear?.(key)}
          onClearAll={showClearAll ? onClearAll : undefined}
        />
      )}
    </div>
  );
}

export default FilterBar;
